/**
 * Click-to-rename title for a pipeline card. Read state matches
 * `CardTitleRow`'s title span (`PipelineCardParts.tsx`); click swaps to a
 * text editor via the shared `useInlineEdit` lifecycle, following
 * `PipelineColumn.tsx`'s stage-rename pattern (interactive `role='button'`
 * title, `useDndContainment` so the card's own click/drag never fires,
 * blur-cancels-on-empty).
 *
 * The permission/schema gate and the optimistic save path live in
 * `useInlineNameRename` / `saveNameRename` (`lib/crm/inline-edit/`) — see
 * those modules' doc comments for the gate rules and the single-combined-
 * event seq-guard trap. This component owns only what's specific to the
 * pipeline card surface: dnd-kit containment, touch fall-through, and the
 * hover pencil.
 *
 * TOUCH: the interactive title is `pointer-events-none` under
 * `(pointer: coarse)` so a tap falls through to the card's own tap-to-open —
 * touch's `TouchSensor` long-press (200ms) is already dnd-kit's drag
 * activation gesture on this surface, and there's no room for a second
 * meaning on a quick tap.
 */

import { Edit03 } from '@untitledui/icons';
import { type KeyboardEvent, useCallback } from 'react';

import { getChromeOverrides } from '@/lib/crm/inline-edit/chromeOverrides';
import {
  NAME_DESCRIPTOR,
  useInlineNameRename,
} from '@/lib/crm/inline-edit/useInlineNameRename';
import type { CrmObject } from '@/types/crm';
import { cx } from '@/utils/cx';

import { CrmFieldValueInput } from '../../components/fields/CrmFieldValueInput';
import type { PropertyTypeSchema } from '../../hooks/usePropertySchema';
import { useDndContainment } from '../utils/dndContainment';

export interface InlineCardTitleProps {
  /** The card entity being renamed. */
  object: CrmObject;
  /** Read-only display text shown when not editing (the card's current title). */
  title: string;
  /** The entity type's property schemas. The renamed property is the one the
   *  display name resolves from; an empty map (still loading), or a display
   *  name composed from several fields, fails closed to read-only. */
  propertySchemas: Readonly<Record<string, PropertyTypeSchema | undefined>>;
}

export function InlineCardTitle({
  object,
  title,
  propertySchemas,
}: InlineCardTitleProps) {
  const {
    canRename,
    isEditing,
    status,
    draft,
    errorMessage,
    announcement,
    startEdit,
    setDraft,
    handleInputKeyDown: inlineEditKeyDown,
    handleBlur: handleEditBlur,
    containerRef,
    triggerRef,
    titleSchema,
  } = useInlineNameRename({ object, propertySchemas });

  const containment = useDndContainment();

  // The card's outer element carries dnd-kit's drag listeners AND its own
  // click-to-open / Enter-to-open handlers (PipelineCard.tsx) — every
  // interaction on the title must be contained here first, or it bubbles
  // into both.
  const handleTitleClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      containment.onClick(e);
      if (!canRename) {
        return;
      }
      startEdit();
    },
    [containment, canRename, startEdit]
  );

  const handleTitleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSpanElement>) => {
      containment.onKeyDown(e);
      if (!canRename) {
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        startEdit();
      }
    },
    [containment, canRename, startEdit]
  );

  // Stop propagation (dnd-kit containment) then handle Escape/Enter via hook.
  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      containment.onKeyDown(e);
      inlineEditKeyDown(e);
    },
    [containment, inlineEditKeyDown]
  );

  const setTriggerRef = useCallback(
    (el: HTMLElement | null) => {
      triggerRef.current = el;
    },
    [triggerRef]
  );

  const disabled = status === 'saving';

  return (
    <div ref={containerRef} className='group/title px-3'>
      <div aria-live='assertive' className='sr-only' aria-atomic='true'>
        {announcement}
      </div>
      {isEditing && titleSchema ? (
        <div
          onBlur={handleEditBlur}
          onKeyDown={handleInputKeyDown}
          onClick={containment.onClick}
          onPointerDown={containment.onPointerDown}
          // The card body is `pointer-events-none` (PipelineCard.tsx) with
          // interactive surfaces opting back in. Without this, clicks on the
          // open editor fall through to the card's absolutely-positioned
          // "Open record" overlay — blurring the input and opening the record.
          className='pointer-events-auto'
        >
          <div className={getChromeOverrides(NAME_DESCRIPTOR.kind, 'sm')}>
            <CrmFieldValueInput
              kind='property'
              schema={titleSchema}
              value={draft as string | number | object | null | undefined}
              onChange={setDraft}
              disabled={disabled}
              size='sm'
            />
          </div>
          {errorMessage && (
            <p
              role='alert'
              className='mt-1 text-xs text-[var(--color-text-error-primary)]'
            >
              {errorMessage}
            </p>
          )}
        </div>
      ) : (
        <div className='flex items-center gap-1 min-w-0'>
          {canRename ? (
            <span
              ref={setTriggerRef}
              role='button'
              tabIndex={0}
              onClick={handleTitleClick}
              onKeyDown={handleTitleKeyDown}
              onPointerDown={containment.onPointerDown}
              aria-label={`Rename ${title}`}
              className={cx(
                'font-semibold text-sm text-[var(--color-text-primary)] line-clamp-2 min-w-0',
                'cursor-text hover:text-[var(--color-text-secondary)] transition-colors',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring rounded-sm',
                'pointer-events-auto',
                // Touch: fall through to the card's own tap-to-open — a quick
                // tap can't disambiguate rename from open, and long-press is
                // already dnd-kit's TouchSensor drag-activation gesture here.
                'pointer-coarse:pointer-events-none'
              )}
            >
              {title}
            </span>
          ) : (
            <span className='font-semibold text-sm text-[var(--color-text-primary)] line-clamp-2 min-w-0'>
              {title}
            </span>
          )}
          {canRename && (
            // Clickable discoverability hint — a pencil that ignores direct
            // clicks reads as disabled. aria-hidden with no tab stop: the
            // title span above is the accessible control; this is a pointer
            // duplicate of it. Touch keeps the fall-through-to-card-open
            // behaviour (same rationale as the span).
            <span
              aria-hidden='true'
              onClick={handleTitleClick}
              onPointerDown={containment.onPointerDown}
              className='hidden group-hover/title:block group-focus-within/title:block shrink-0 cursor-pointer pointer-events-auto pointer-coarse:pointer-events-none'
            >
              <Edit03 className='size-3.5 text-[var(--color-text-tertiary)]' />
            </span>
          )}
        </div>
      )}
    </div>
  );
}
